import { Box, Typography } from '@mui/material';
import { sections } from '../data/homepage';
import { Container, Section, SectionHeader, Text } from './atomic';

/**
 * Our Approach dark section component
 *
 * This component displays the two-column approach content in dark theme. The
 * heading and column copy come from the CMS (src/content/homepage.json →
 * sections.approach); the green corner accents alternate per column.
 *
 * @returns {JSX.Element} Our Approach section with dark theme
 */
function OurApproachDark() {
  const { heading, columns } = sections.approach;

  return (
    <Section
      dark
      sx={{
        backgroundColor: 'var(--color-secondary)',
        backgroundImage: 'url(/diverse-team-collaboration.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        py: 0,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(26, 26, 26, 0.85)',
          zIndex: 0,
        },
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      <SectionHeader title={heading} dark />

      <Container maxWidth="lg" sx={{ pb: 8 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 4,
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
          }}
        >
          {columns.map((column, columnIndex) => {
            const isEven = columnIndex % 2 === 0;
            return (
              <Box key={column.heading} sx={{ flex: '1 1 50%', minWidth: 0 }}>
                <Box
                  sx={{
                    backgroundColor:
                      'color-mix(in srgb, var(--color-primary) 15%, transparent)',
                    p: 4,
                    borderRadius: '12px',
                    border:
                      '1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)',
                    height: '100%',
                    width: '100%',
                    flex: 1,
                    position: 'relative',
                    // Green corners alternate: even columns top-left and
                    // bottom-right, odd columns top-right and bottom-left
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -1,
                      left: isEven ? -1 : 'auto',
                      right: !isEven ? -1 : 'auto',
                      width: '50px',
                      height: '50px',
                      borderTop: '3px solid var(--color-primary)',
                      borderLeft: isEven
                        ? '3px solid var(--color-primary)'
                        : 'none',
                      borderRight: !isEven
                        ? '3px solid var(--color-primary)'
                        : 'none',
                      borderRadius: 0,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -1,
                      right: isEven ? -1 : 'auto',
                      left: !isEven ? -1 : 'auto',
                      width: '50px',
                      height: '50px',
                      borderBottom: '3px solid var(--color-primary)',
                      borderRight: isEven
                        ? '3px solid var(--color-primary)'
                        : 'none',
                      borderLeft: !isEven
                        ? '3px solid var(--color-primary)'
                        : 'none',
                      borderRadius: 0,
                    },
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      mb: 3,
                      fontWeight: 600,
                      fontSize: '1.5rem',
                      lineHeight: 1.3,
                      textAlign: 'center',
                    }}
                  >
                    {column.heading}
                  </Typography>

                  {column.paragraphs.map((paragraph, paragraphIndex) => (
                    <Text
                      key={`${column.heading}-${paragraphIndex}`}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        lineHeight: 1.7,
                        fontSize: '1rem',
                        mb:
                          paragraphIndex < column.paragraphs.length - 1 ? 3 : 0,
                      }}
                    >
                      {paragraph}
                    </Text>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Section>
  );
}

export default OurApproachDark;
